/**
 * Mapping between public tool slugs (the TOOLS registry) and Studio
 * workbench registry ids. Shared by the app-host `/tools/:slug` redirect
 * (tools-app) and the ⌘K palette, so both agree on which slugs a mounted
 * Studio can open and which page a Studio tool has.
 */

import { Effect } from 'effect';

// Renames between the public tool slugs and Studio registry ids.
const STUDIO_SLUG_ALIASES: Record<string, string> = {
  'jpg-to-pdf': 'image-to-pdf',
  'png-to-pdf': 'image-to-pdf',
  'pdf-to-jpg': 'pdf-to-image',
  'pdf-to-png': 'pdf-to-image',
};

// Public slugs that share their id with a Studio registry tool.
const STUDIO_TOOL_IDS = new Set([
  'merge',
  'split',
  'rotate',
  'organize',
  'sign',
  'bates',
  'crop',
  'compress',
  'compare',
  'watermark',
  'page-numbers',
  'metadata',
  'flatten',
  'redact',
  'ocr',
  'word-to-pdf',
  'excel-to-pdf',
  'pdf-to-word',
  // Cloud in the host app: the extra tool of this id replaces the plugin's
  // local panel (W.4.111).
  'pdf-to-excel',
  'pdf-to-markdown',
  // Cloud-backed tools — injected into the workbench via `StudioApp
  // extraTools` (features/tools-app/extra-tools), so they resolve under their
  // public slug just like built-ins (W.4.39).
  'pdf-to-json',
  'verapdf-checker',
  'matterhorn-pdf-checker',
  'well-tagged-pdf-checker',
  'pdf-to-html',
  'pdf-to-link',
  'handwriting-ocr',
  // Local extras — runner existed only as a `/tools/<slug>` page (W.4.40).
  'highlight',
]);

/** Studio registry id for a public slug, or null when the Studio has no such tool. */
export const studioToolIdForSlugEffect = Effect.fn('web.studioTool.resolveId')(function* (
  slug: string,
) {
  return STUDIO_SLUG_ALIASES[slug] ?? (STUDIO_TOOL_IDS.has(slug) ? slug : null);
});

export function studioToolIdForSlug(slug: string): string | null {
  return Effect.runSync(studioToolIdForSlugEffect(slug));
}

/**
 * The public `/tools/<slug>` page of a Studio tool, or null when it has none.
 * Where two slugs share a tool (JPG and PNG to PDF) the first alias is its page
 * unless a search names the other one (`matchTools`).
 */
export const publicSlugForStudioToolEffect = Effect.fn('web.studioTool.resolveSlug')(function* (
  toolId: string,
) {
  const alias = Object.keys(STUDIO_SLUG_ALIASES).find(
    (slug) => STUDIO_SLUG_ALIASES[slug] === toolId,
  );
  return alias ?? (STUDIO_TOOL_IDS.has(toolId) ? toolId : null);
});

export function publicSlugForStudioTool(toolId: string): string | null {
  return Effect.runSync(publicSlugForStudioToolEffect(toolId));
}
